import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus,
  Trash,
  Buildings,
  CaretDown,
  CaretUp,
  X,
  BookmarkSimple,
} from "@phosphor-icons/react";

const emptyRoom = (colCount) => ({ room_type: "", adults: 0, rates: Array(colCount).fill(0) });
const emptyHotel = (colCount) => ({ hotel_name: "", hotel_address: "", brand: "", currency: "USD", inclusions: [], rooms: [emptyRoom(colCount)] });
const emptyCity = (colCount) => ({
  id: crypto.randomUUID(),
  city: "",
  country: "",
  check_in: "",
  check_out: "",
  nights: 0,
  hotels: [emptyHotel(colCount)],
});

const GridProposalEditor = ({ gridData, onChange, inclusionsLibrary = [], defaultInclusions = [] }) => {
  const columns = gridData?.columns || ["Non-Refundable", "Refundable", "W/o Breakfast"];
  const cities = gridData?.cities || [];
  const [expandedCities, setExpandedCities] = useState(() => cities.map((_, i) => i));

  const update = (newData) => onChange({ ...gridData, ...newData });

  // Column management
  const updateColumn = (idx, val) => {
    const newCols = columns.map((c, i) => (i === idx ? val : c));
    update({ columns: newCols });
  };

  const addColumn = () => {
    const newCols = [...columns, `Rate ${columns.length + 1}`];
    const newCities = cities.map((city) => ({
      ...city,
      hotels: city.hotels.map((h) => ({
        ...h,
        rooms: h.rooms.map((r) => ({ ...r, rates: [...r.rates, 0] })),
      })),
    }));
    update({ columns: newCols, cities: newCities });
  };

  const removeColumn = (idx) => {
    if (columns.length <= 1) return;
    const newCols = columns.filter((_, i) => i !== idx);
    const newCities = cities.map((city) => ({
      ...city,
      hotels: city.hotels.map((h) => ({
        ...h,
        rooms: h.rooms.map((r) => ({ ...r, rates: r.rates.filter((_, i) => i !== idx) })),
      })),
    }));
    update({ columns: newCols, cities: newCities });
  };

  // City management
  const addCity = () => {
    const newIdx = cities.length;
    update({ cities: [...cities, emptyCity(columns.length)] });
    setExpandedCities((prev) => [...prev, newIdx]);
  };
  const removeCity = (idx) => update({ cities: cities.filter((_, i) => i !== idx) });

  const updateCity = (ci, field, val) => {
    const newCities = cities.map((c, i) => {
      if (i !== ci) return c;
      const updated = { ...c, [field]: val };
      if (field === "check_in" || field === "check_out") {
        if (updated.check_in && updated.check_out) {
          const diff = Math.round((new Date(updated.check_out) - new Date(updated.check_in)) / 86400000);
          if (diff > 0) updated.nights = diff;
        }
      }
      return updated;
    });
    update({ cities: newCities });
  };

  // Hotel management
  const addHotel = (ci) => {
    const newCities = cities.map((c, i) =>
      i === ci ? { ...c, hotels: [...c.hotels, emptyHotel(columns.length)] } : c
    );
    update({ cities: newCities });
  };

  const removeHotel = (ci, hi) => {
    const newCities = cities.map((c, i) =>
      i === ci ? { ...c, hotels: c.hotels.filter((_, j) => j !== hi) } : c
    );
    update({ cities: newCities });
  };

  const updateHotel = (ci, hi, field, val) => {
    const newCities = cities.map((c, i) =>
      i === ci
        ? { ...c, hotels: c.hotels.map((h, j) => (j === hi ? { ...h, [field]: val } : h)) }
        : c
    );
    update({ cities: newCities });
  };

  // Room management
  const addRoom = (ci, hi) => {
    const newCities = cities.map((c, i) =>
      i === ci
        ? {
            ...c,
            hotels: c.hotels.map((h, j) =>
              j === hi ? { ...h, rooms: [...h.rooms, emptyRoom(columns.length)] } : h
            ),
          }
        : c
    );
    update({ cities: newCities });
  };

  const removeRoom = (ci, hi, ri) => {
    const newCities = cities.map((c, i) =>
      i === ci
        ? {
            ...c,
            hotels: c.hotels.map((h, j) =>
              j === hi ? { ...h, rooms: h.rooms.filter((_, k) => k !== ri) } : h
            ),
          }
        : c
    );
    update({ cities: newCities });
  };

  const updateRoom = (ci, hi, ri, field, val) => {
    const newCities = cities.map((c, i) =>
      i === ci
        ? {
            ...c,
            hotels: c.hotels.map((h, j) =>
              j === hi
                ? { ...h, rooms: h.rooms.map((r, k) => (k === ri ? { ...r, [field]: val } : r)) }
                : h
            ),
          }
        : c
    );
    update({ cities: newCities });
  };

  const updateRate = (ci, hi, ri, colIdx, val) => {
    const newCities = cities.map((c, i) =>
      i === ci
        ? {
            ...c,
            hotels: c.hotels.map((h, j) =>
              j === hi
                ? {
                    ...h,
                    rooms: h.rooms.map((r, k) =>
                      k === ri ? { ...r, rates: r.rates.map((rv, ci2) => (ci2 === colIdx ? val : rv)) } : r
                    ),
                  }
                : h
            ),
          }
        : c
    );
    update({ cities: newCities });
  };

  const toggleHotelInclusion = (ci, hi, inc) => {
    const hotel = cities[ci]?.hotels[hi];
    if (!hotel) return;
    const incs = hotel.inclusions || [];
    const newIncs = incs.includes(inc) ? incs.filter((i) => i !== inc) : [...incs, inc];
    updateHotel(ci, hi, "inclusions", newIncs);
  };

  const toggleCity = (idx) => {
    setExpandedCities((prev) => (prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]));
  };

  return (
    <div className="space-y-4" data-testid="grid-proposal-editor">
      {/* Column Headers Editor */}
      <div className="bg-white border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rate Columns</Label>
          <Button variant="ghost" size="sm" onClick={addColumn} className="gap-1 text-xs h-6 text-gold" data-testid="add-column-btn">
            <Plus size={12} /> Add Column
          </Button>
        </div>
        <div className="flex gap-2 flex-wrap">
          {columns.map((col, idx) => (
            <div key={idx} className="flex items-center gap-1">
              <Input
                value={col}
                onChange={(e) => updateColumn(idx, e.target.value)}
                className="text-xs h-8 w-36 bg-cream/50 border-border"
                placeholder={`Column ${idx + 1}`}
                data-testid={`column-name-${idx}`}
              />
              {columns.length > 1 && (
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-600" onClick={() => removeColumn(idx)}>
                  <Trash size={12} />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Cities */}
      {cities.map((city, ci) => (
        <div key={city.id || ci} className="bg-white border border-border" data-testid={`grid-city-${ci}`}>
          {/* City header */}
          <div
            className="flex items-center justify-between p-3 bg-navy/5 cursor-pointer"
            onClick={() => toggleCity(ci)}
          >
            <div className="flex items-center gap-2">
              {expandedCities.includes(ci) ? <CaretUp size={14} /> : <CaretDown size={14} />}
              <span className="text-sm font-semibold text-navy">
                {city.city || `City ${ci + 1}`}
                {city.country && <span className="text-xs text-muted-foreground ml-2">{city.country}</span>}
              </span>
              {city.nights > 0 && <span className="text-[10px] bg-gold/10 text-gold px-2 py-0.5">{city.nights}N</span>}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-muted-foreground">{city.hotels?.length || 0} hotel{(city.hotels?.length || 0) !== 1 ? "s" : ""}</span>
              {cities.length > 1 && (
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-600" onClick={(e) => { e.stopPropagation(); removeCity(ci); }}>
                  <Trash size={12} />
                </Button>
              )}
            </div>
          </div>

          {expandedCities.includes(ci) && (
            <div className="p-4 space-y-4">
              {/* City details */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">City</Label>
                  <Input value={city.city} onChange={(e) => updateCity(ci, "city", e.target.value)} placeholder="e.g. Paris" className="text-xs h-8" data-testid={`grid-city-name-${ci}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Country</Label>
                  <Input value={city.country} onChange={(e) => updateCity(ci, "country", e.target.value)} placeholder="France" className="text-xs h-8" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Check-in</Label>
                  <Input type="date" value={city.check_in} onChange={(e) => updateCity(ci, "check_in", e.target.value)} className="text-xs h-8" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Check-out</Label>
                  <Input type="date" value={city.check_out} onChange={(e) => updateCity(ci, "check_out", e.target.value)} className="text-xs h-8" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Nights</Label>
                  <Input type="number" value={city.nights || ""} onChange={(e) => updateCity(ci, "nights", parseInt(e.target.value) || 0)} className="text-xs h-8" />
                </div>
              </div>

              {/* Hotels in city */}
              {city.hotels.map((hotel, hi) => (
                <div key={hi} className="border border-border bg-cream/20 p-3" data-testid={`grid-hotel-${ci}-${hi}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-navy">
                      {city.hotels.length > 1 ? `Option ${hi + 1}` : "Hotel"}
                      {hotel.hotel_name && ` — ${hotel.hotel_name}`}
                    </span>
                    <div className="flex gap-1">
                      {city.hotels.length > 1 && (
                        <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground hover:text-red-600" onClick={() => removeHotel(ci, hi)}>
                          <Trash size={12} />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Hotel Name</Label>
                      <Input value={hotel.hotel_name} onChange={(e) => updateHotel(ci, hi, "hotel_name", e.target.value)} placeholder="e.g. Mandarin Oriental" className="text-xs h-8" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Address</Label>
                      <Input value={hotel.hotel_address} onChange={(e) => updateHotel(ci, hi, "hotel_address", e.target.value)} placeholder="251 Rue Saint Honoré..." className="text-xs h-8" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Currency</Label>
                      <Input value={hotel.currency || "USD"} onChange={(e) => updateHotel(ci, hi, "currency", e.target.value)} placeholder="USD" className="text-xs h-8" list={`curr-list-${ci}-${hi}`} />
                      <datalist id={`curr-list-${ci}-${hi}`}>
                        {["USD","EUR","GBP","INR","AED","AUD","SGD","THB","JPY","CHF","HRK","IDR"].map((c) => <option key={c} value={c} />)}
                      </datalist>
                    </div>
                  </div>

                  {/* Rate Table */}
                  <div className="overflow-x-auto mb-2">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-navy text-white">
                          <th className="text-left px-2 py-1.5 text-[10px] font-semibold">Room Type</th>
                          <th className="text-center px-1 py-1.5 text-[10px] font-semibold w-16">Adults</th>
                          {columns.map((col, colIdx) => (
                            <th key={colIdx} className="text-center px-2 py-1.5 text-[10px] font-semibold">{col}</th>
                          ))}
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {hotel.rooms.map((room, ri) => (
                          <tr key={ri} className="border-b border-border">
                            <td className="px-1 py-1">
                              <Input
                                value={room.room_type}
                                onChange={(e) => updateRoom(ci, hi, ri, "room_type", e.target.value)}
                                placeholder="e.g. Deluxe King"
                                className="text-xs h-7 border-0 bg-transparent px-1"
                              />
                            </td>
                            <td className="px-1 py-1">
                              <Input
                                type="number"
                                value={room.adults || ""}
                                onChange={(e) => updateRoom(ci, hi, ri, "adults", parseInt(e.target.value) || 0)}
                                placeholder="0"
                                className="text-xs h-7 border-0 bg-transparent text-center px-1 w-14"
                                min={0}
                              />
                            </td>
                            {columns.map((_, colIdx) => (
                              <td key={colIdx} className="px-1 py-1">
                                <Input
                                  type="number"
                                  value={room.rates?.[colIdx] || ""}
                                  onChange={(e) => updateRate(ci, hi, ri, colIdx, parseFloat(e.target.value) || 0)}
                                  placeholder="0"
                                  className="text-xs h-7 border-0 bg-transparent text-center px-1"
                                />
                              </td>
                            ))}
                            <td className="px-1 py-1 text-center">
                              {hotel.rooms.length > 1 && (
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-600" onClick={() => removeRoom(ci, hi, ri)}>
                                  <Trash size={10} />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => addRoom(ci, hi)} className="gap-1 text-xs h-6 text-gold" data-testid={`add-room-${ci}-${hi}`}>
                    <Plus size={10} /> Add Room Row
                  </Button>

                  {/* Hotel Inclusions — same as detailed mode */}
                  <div className="mt-3 pt-2 border-t border-border">
                    <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">Inclusions for this hotel</Label>

                    {/* Standard inclusions */}
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Standard</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-2">
                      {defaultInclusions.map((inc, incIdx) => (
                        <div key={incIdx} className="flex items-center gap-1.5">
                          <Checkbox
                            id={`grid-${ci}-${hi}-inc-${incIdx}`}
                            checked={(hotel.inclusions || []).includes(inc)}
                            onCheckedChange={() => toggleHotelInclusion(ci, hi, inc)}
                            className="border-border data-[state=checked]:bg-navy data-[state=checked]:border-navy h-3.5 w-3.5"
                          />
                          <label htmlFor={`grid-${ci}-${hi}-inc-${incIdx}`} className="text-[11px] cursor-pointer leading-tight">{inc}</label>
                        </div>
                      ))}
                    </div>

                    {/* Library inclusions */}
                    {(() => {
                      const stdSet = new Set(defaultInclusions);
                      const extras = (inclusionsLibrary || []).filter((li) => !stdSet.has(li.text));
                      if (extras.length === 0) return null;
                      return (
                        <>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 mt-2">From Your Library</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-2">
                            {extras.map((li) => (
                              <div key={li.id} className="flex items-center gap-1.5">
                                <Checkbox
                                  id={`grid-${ci}-${hi}-lib-${li.id}`}
                                  checked={(hotel.inclusions || []).includes(li.text)}
                                  onCheckedChange={() => toggleHotelInclusion(ci, hi, li.text)}
                                  className="border-border data-[state=checked]:bg-gold data-[state=checked]:border-gold h-3.5 w-3.5"
                                />
                                <label htmlFor={`grid-${ci}-${hi}-lib-${li.id}`} className="text-[11px] cursor-pointer leading-tight">{li.text}</label>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}

                    {/* Custom inclusions */}
                    {(() => {
                      const stdSet = new Set(defaultInclusions);
                      const libSet = new Set((inclusionsLibrary || []).map((li) => li.text));
                      const customs = (hotel.inclusions || []).filter((inc) => !stdSet.has(inc) && !libSet.has(inc));
                      if (customs.length === 0) return null;
                      return (
                        <div className="mb-2">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Custom</p>
                          <div className="flex flex-wrap gap-1">
                            {customs.map((inc) => (
                              <span key={inc} className="inline-flex items-center gap-1 bg-cream text-[10px] px-2 py-0.5 border border-border">
                                {inc}
                                <button onClick={() => toggleHotelInclusion(ci, hi, inc)} className="text-muted-foreground hover:text-red-600"><X size={10} /></button>
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Add custom inclusion */}
                    <div className="flex gap-1 mt-1">
                      <Input
                        placeholder="Add custom inclusion..."
                        className="text-[11px] h-7 flex-1"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.target.value.trim()) {
                            toggleHotelInclusion(ci, hi, e.target.value.trim());
                            e.target.value = "";
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={() => addHotel(ci)} className="gap-1.5 text-xs w-full" data-testid={`add-hotel-option-${ci}`}>
                <Buildings size={14} /> Add Hotel Option
              </Button>
            </div>
          )}
        </div>
      ))}

      {/* Add City */}
      <Button variant="outline" onClick={addCity} className="gap-1.5 text-xs w-full" data-testid="add-grid-city">
        <Plus size={14} /> Add City
      </Button>
    </div>
  );
};

export default GridProposalEditor;
